import LoadingScreen from "common/components/LoadingScreen";
import { useIsMobile } from "common/utils";
import { restoreToken, tokenValid } from "common/utils/token";
import { Farm } from "model";
import { ReactNode, useEffect, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { initializeApp } from "services";
import { useAppStore } from "stores";
import { initializeForRoute } from "./hooks";

interface PrivateRouteProps {
  children: ReactNode;
}

/**
 * PrivateRoute - Handles authentication and initial app setup.
 *
 * This component:
 * 1. Checks if the user has a valid token
 * 2. If token exists but user isn't logged in, refreshes the session
 * 3. Uses route config to determine which farm to initialize
 * 4. Redirects to login if no valid session
 */
const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const location = useLocation();
  // Use Zustand for auth state
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  const loading = useAppStore((state) => state.loading);
  const isLoading = loading === "pending";
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const refreshingRef = useRef(false);

  restoreToken(location); // trying to restore the token

  // Handle token refresh in useEffect to avoid setState during render
  const needsRefresh = tokenValid() && !isAuthenticated;

  useEffect(() => {
    if (!needsRefresh || refreshingRef.current) return;
    refreshingRef.current = true;

    const doRefresh = async () => {
      let farms: Farm[] | undefined = [];
      let isCorporateUser = false;
      let needsFarmInit = false;

      try {
        // Pass skipLoadingStateReset: true to keep loading state active
        // throughout both app init and farm init phases
        const res = await initializeApp({ skipLoadingStateReset: true });
        farms = res?.farms;
        needsFarmInit = res?.needsFarmInit ?? false;
        isCorporateUser =
          !!res?.user?.corporations?.length &&
          res?.user?.corporations?.length > 0;
      } catch (e) {
        console.log(e);
        navigate(`/login?from=${location?.pathname}`);
        return;
      }

      // Use route config to determine initialization behavior
      // This will handle farm initialization and set loading to idle when complete
      await initializeForRoute({
        pathname: location.pathname,
        farms: farms ?? [],
        isCorporateUser,
        isMobile,
        navigate,
        needsFarmInit,
      });
    };

    (async () => {
      await doRefresh();
    })().catch((error) => {
      console.error("Failed to refresh route", error);
    });
  }, [needsRefresh, navigate, isMobile, location]);

  if (isAuthenticated && !isLoading) {
    return <>{children}</>;
  } else if (needsRefresh || isLoading) {
    // Show loading screen while refreshing token, initializing app, or loading farm data
    return <LoadingScreen />;
  } else {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
};

export default PrivateRoute;
